import type { AuthGrant as NestAuthGrant } from "../nest/generated"

/**
 * People are colleagues whose computers shared a project with this client, or
 * who hold access to one of this client's computers. Their connection
 * (routes and token) lives in the ordinary computer registry; this record only
 * adds the non-secret presentation and cached level on top.
 */

export type AccessLevel = "view" | "propose" | "operate" | "admin"

export const ACCESS_LEVELS: readonly AccessLevel[] = ["view", "propose", "operate", "admin"]

export const ACCESS_LEVEL_LABELS: Record<AccessLevel, { label: string; summary: string }> = {
    view: { label: "View", summary: "See threads, diffs, and files in this project." },
    propose: { label: "Propose", summary: "View, plus suggest work that you approve before it runs." },
    operate: { label: "Operate", summary: "Start agents and run commands in this project on your computer." },
    admin: { label: "Admin", summary: "Everything on this computer, exactly like you." },
}

/** "none" is a linked colleague who has not been granted anything yet. */
export type PersonLevel = AccessLevel | "none"

export interface PersonRecord {
    nodeId: string
    personName: string
    glyph?: string
    color?: string
    principalId?: string
    level: PersonLevel
    projectNames: string[]
    addedAt: string
}

export interface PeopleRecordState {
    version: 1
    people: PersonRecord[]
}

const PEOPLE_KEY = "wildlark:people:v1"

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function nonEmptyString(value: unknown): string | null {
    return typeof value === "string" && value.trim() ? value.trim() : null
}

export function isAccessLevel(value: unknown): value is AccessLevel {
    return typeof value === "string" && (ACCESS_LEVELS as readonly string[]).includes(value)
}

export function isPersonLevel(value: unknown): value is PersonLevel {
    return value === "none" || isAccessLevel(value)
}

export function personLevelLabel(level: PersonLevel): string {
    return level === "none" ? "No access" : ACCESS_LEVEL_LABELS[level].label
}

export function parsePersonRecord(value: unknown): PersonRecord | null {
    if (!isRecord(value)) return null
    const nodeId = nonEmptyString(value.nodeId)
    const personName = nonEmptyString(value.personName)
    const addedAt = nonEmptyString(value.addedAt)
    if (!nodeId || !personName || !addedAt || !isPersonLevel(value.level)) return null
    const glyph = nonEmptyString(value.glyph) ?? undefined
    const color = nonEmptyString(value.color) ?? undefined
    const principalId = nonEmptyString(value.principalId) ?? undefined
    const projectNames = Array.isArray(value.projectNames) ? value.projectNames.map(nonEmptyString).filter((name): name is string => name !== null) : []
    return {
        nodeId,
        personName,
        level: value.level,
        projectNames,
        addedAt,
        ...(glyph ? { glyph } : {}),
        ...(color ? { color } : {}),
        ...(principalId ? { principalId } : {}),
    }
}

export function parsePeopleState(value: unknown): PeopleRecordState {
    if (!isRecord(value) || value.version !== 1 || !Array.isArray(value.people)) return { version: 1, people: [] }
    return { version: 1, people: value.people.map(parsePersonRecord).filter((person): person is PersonRecord => person !== null) }
}

export function readPeople(storage: Pick<Storage, "getItem"> = window.localStorage): PersonRecord[] {
    try {
        const raw = storage.getItem(PEOPLE_KEY)
        return raw ? parsePeopleState(JSON.parse(raw) as unknown).people : []
    } catch {
        return []
    }
}

export function writePerson(person: PersonRecord, storage: Pick<Storage, "getItem" | "setItem"> = window.localStorage): PersonRecord[] {
    const people = readPeople(storage).filter((existing) => existing.nodeId !== person.nodeId)
    people.push(person)
    storage.setItem(PEOPLE_KEY, JSON.stringify({ version: 1, people } satisfies PeopleRecordState))
    return people
}

export function removePerson(nodeId: string, storage: Pick<Storage, "getItem" | "setItem"> = window.localStorage): PersonRecord[] {
    const people = readPeople(storage).filter((existing) => existing.nodeId !== nodeId)
    storage.setItem(PEOPLE_KEY, JSON.stringify({ version: 1, people } satisfies PeopleRecordState))
    return people
}

/**
 * Grants for one level on a set of projects. Admin is node-wide and ignores
 * the project list; every other level is workspace-scoped. Propose is
 * deliberately identical to view on the node: proposals are written through
 * the WildLark module with the owner's authority, never by a guest write grant.
 */
export function grantsForLevel(level: AccessLevel, workspaceIds: readonly string[], expiresAt?: string): NestAuthGrant[] {
    const expiry = expiresAt ? { expiresAt } : {}
    if (level === "admin") {
        return (["read", "write", "exec", "agent", "admin"] as const).map((capability) => ({ capability, scope: { type: "node" }, ...expiry }))
    }
    const capabilities = level === "operate" ? (["read", "write", "exec", "agent"] as const) : (["read"] as const)
    const grants: NestAuthGrant[] = []
    for (const workspaceId of workspaceIds) {
        for (const capability of capabilities) {
            grants.push({ capability, scope: { type: "workspace", workspaceId }, ...expiry })
        }
    }
    return grants
}

export interface GrantSummary {
    node: boolean
    level: AccessLevel | "none"
    workspaceIds: string[]
    expiresAt?: string
}

/**
 * Collapses raw grants into the level a client should present. A mixed set
 * reports the strongest level any workspace holds; the exact per-workspace
 * capabilities still gate individual calls on the node.
 */
export function summarizeGrants(grants: readonly NestAuthGrant[] | undefined, markProposeLevel = false): GrantSummary {
    if (!grants || grants.length === 0) return { node: false, level: "none", workspaceIds: [] }
    const node = grants.some((grant) => grant.scope.type === "node" && grant.capability === "read")
    if (node) {
        const admin = grants.some((grant) => grant.scope.type === "node" && grant.capability === "admin")
        const expiresAt = earliestExpiry(grants)
        return { node: true, level: admin ? "admin" : "operate", workspaceIds: [], ...(expiresAt ? { expiresAt } : {}) }
    }
    const byWorkspace = new Map<string, Set<string>>()
    for (const grant of grants) {
        if (grant.scope.type !== "workspace" || !grant.scope.workspaceId) continue
        const set = byWorkspace.get(grant.scope.workspaceId) ?? new Set<string>()
        set.add(grant.capability)
        byWorkspace.set(grant.scope.workspaceId, set)
    }
    const workspaceIds = [...byWorkspace.keys()].sort()
    const operate = [...byWorkspace.values()].some((set) => set.has("agent") && set.has("write") && set.has("exec"))
    const expiresAt = earliestExpiry(grants)
    const level: AccessLevel | "none" = workspaceIds.length === 0 ? "none" : operate ? "operate" : markProposeLevel ? "propose" : "view"
    return { node: false, level, workspaceIds, ...(expiresAt ? { expiresAt } : {}) }
}

function earliestExpiry(grants: readonly NestAuthGrant[]): string | undefined {
    let earliest: string | undefined
    for (const grant of grants) {
        if (!grant.expiresAt) continue
        if (!earliest || grant.expiresAt < earliest) earliest = grant.expiresAt
    }
    return earliest
}

export function personInitials(name: string): string {
    const parts = name
        .trim()
        .split(/\s+/)
        .filter((part) => part.length > 0)
    if (parts.length === 0) return "?"
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
    return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase()
}

/** The chat-ready message for a share link. Short enough for a Telegram or Slack paste. */
export function shareMessage({ from, project, level, url }: { from?: string; project?: string; level: AccessLevel; url: string }): string {
    const who = from ? `${from} shared` : "Someone shared"
    const what = project ? `“${project}”` : "a computer"
    return `${who} ${what} with you on WildLark (${ACCESS_LEVEL_LABELS[level].label.toLowerCase()} access). Open to accept:\n${url}`
}
